import { HttpErrorResponse, HttpResponse } from "@angular/common/http"
import { BehaviorSubject, Observable, Subscription } from "rxjs"
import { Attachment } from "src/app/_interfaces/Attachment"
import { ContactPerson } from "src/app/_interfaces/ContactPerson"
import { EditArt } from "src/app/_interfaces/EditArt"
import { HttpFunctionResult } from "src/app/_interfaces/HttpFunctionResult"
import { Ticket, TicketBase } from "src/app/_interfaces/ticket"
import { WorkstepBase } from "src/app/_interfaces/workstep"
import { NewTicket } from "src/app/_template/popup-content/add-ticket/add-ticket.page"
import { WorkstepContent } from "src/app/_template/ticket-detail/workstep-content/workstep-content.component"
import { HttpService } from "../http.service"
import dayjs from '../../_utils/dayjs.config';
import { TicketZeit } from "src/app/_interfaces/TicketTime"
import { TimeTicketBundle } from "../zeiten.service"
import { TicketTag, TicketTagColored } from "../boards/board.service"
import { DarkmodeService } from "../darkmode.service"
import { ISocketUser, SocketMessageData, SocketNewWorkstep, SocketUser, SocketsService } from "../sockets.service"
import * as signalR from "@microsoft/signalr"
import { AuthService } from "../auth.service"
import { GeneralService } from "../general.service"
import { ITicketState } from "src/app/_interfaces/TicketState"

export interface WorkstepContentWithAttachment{
    type: "attachment" | "content",
    content?: WorkstepContent,
    attachment?: Attachment
}


export type TicketDetailChangedType = "STATE_CHANGE" | "NAME_CHANGED"
export type TicketDetailChangedData = string | ITicketState
export interface TicketDetailChanged{
    type: TicketDetailChangedType
    ticketId: number
    data: TicketDetailChangedData
}

export abstract class TicketServiceBase{

    // public ticketSubscribtion: Subscription

    protected _worksteps: WorkstepBase[] = []
    protected _workstepContent: WorkstepContent[] = []
    protected _newWorkstepSelected: BehaviorSubject<number> = new BehaviorSubject<number>(null)
    protected _activeWorkstepId: number = 0
    protected _ticketArt: EditArt
    protected _process: number
    protected _kontaktart: EditArt
    protected _berechnen: EditArt
    protected _contactPerson: ContactPerson
    protected _targetTime: number = 0
    protected _tags: TicketTagColored[] = []
    protected _runningExternalTime: TimeTicketBundle[] = []

    protected _workstepContentWithAttachment: WorkstepContentWithAttachment[] = []
    private _workstepContentsOnEdit : WorkstepContent[] = [];
    public workstepCanMoreLoad: boolean = false

    public socketUser: SocketUser[] = []
    public lastTicketId: number | null = null


    public contentOffset: number = 0

    public ticketDetailChanged: (data: TicketDetailChanged) => void = () => {}

    protected _ticket: Ticket = {
        timesGrouped: [],
        times: [],
        attachments: []
    }

    constructor(
        public http?: HttpService,
        public darkmode?: DarkmodeService,
        public socketSrv?: SocketsService,
        public auth?: AuthService,
        public general?: GeneralService
    ){
        if(this.general) this.socketSrv.serverVersionGreaterThan = this.general.serverVersionGreaterThan.bind(this.general)
    }


    public get ticket(): Ticket{
        return this._ticket
    }

    public get worksteps(): WorkstepBase[]{
        return this._worksteps
    }
    public get workstepContent(): WorkstepContent[]{
        return this._workstepContent
    }
    
    public get newWorkstepSelected(): Observable<number>{
        return this._newWorkstepSelected.asObservable()
    }
    public get activeWorkstepId(){
        return this._activeWorkstepId
    }
    public get ticketArt(): EditArt{
        return this._ticketArt
    }
    public set ticketArt( art: EditArt){
        this._ticketArt = art
    }
    public get berechnen(): EditArt{
        return this._berechnen
    }
    public set berechnen( art: EditArt){
        this._berechnen = art
    }
    public get kontaktArt(): EditArt{
        return this._kontaktart
    }
    public set kontaktArt( art: EditArt){
        this._kontaktart = art
    }
    public get process(): number{
        return this._process
    }
    public set process( process: number){
        this._process = process
    }
    public get contactPerson(){
        return this._contactPerson
    }

    public set contactPerson(contactPerson: ContactPerson){
        this._contactPerson = contactPerson
    }

    public get workstepContentsOnEdit() : WorkstepContent[] {
      return this._workstepContentsOnEdit;
    }
    public set workstepContentsOnEdit(v : WorkstepContent[]) {
      this._workstepContentsOnEdit = v;
    }
    public get hasWorkstepContentEditValue(){
      return this._workstepContentsOnEdit.find(c => c.workstepId == this.activeWorkstepId) != null
    }
    public get workstepContentWithAttachment(): WorkstepContentWithAttachment[]{
        return this._workstepContentWithAttachment
    }

    public get tags(): TicketTagColored[]{
        return this._tags
    }

    public set tags(tags: TicketTagColored[]){
        this._tags = tags
    }

    public get targetTime(): number{
        return this._targetTime
    }

    public get runningExternalTime(): TimeTicketBundle[]{
        return this._runningExternalTime
    }

    public get targetTimeString(): string{
        const totalSeconds = this.targetTime
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        
        const hoursString = hours.toString().padStart(2, '0')
        const minutesString = minutes.toString().padStart(2, '0')
        const secondsString = seconds.toString().padStart(2, '0')
        
        return `${hoursString}:${minutesString}:${secondsString} Std.`
    }

    public set targetTime(target: number){
        this._targetTime = target
    }
      
    /**
     * Gibt daten von server Zurück
     * @param {string} path Pfad zum Server
     * @returns 
     */
    protected async getAPIData<T>(path: string): Promise<HttpFunctionResult<T>>{
        const result: HttpFunctionResult<T> = await this.http.GET<T>({path: path})
        if(!result.success) return {success: false}
        return {success: true,data: result.data}
    }

    /**
     * Gibt alle Worksteps anhand der TicketID zurück
     * @param {number} id Id des Tickets
     * @returns {Promise<HttpFunctionResult<WorkstepBase[]>>}
     */
    public async getWorksteps(id: number): Promise<HttpFunctionResult<WorkstepBase[]>>{
        const result: HttpFunctionResult<WorkstepBase[]> = await this.getAPIData<WorkstepBase[]>(`ticket/${id}/worksteps`)
        if(result.success)
            this._worksteps = result.data as WorkstepBase[]
        return result
    }

    /**
     * Löscht die liste der Workstep Contents
     */
    public clearWorkstepContentList(){
        this._workstepContent = []
        this._newWorkstepSelected.next(null)
    }

    /**
     * Setzt alle neuen Instanzen des übergeben Worksteps
     * @param {number} id id des Worksteps 
     * @returns {Promise<void>}
     */
    public async setNewWorkstepAttributes(id: number, runningTimeIds: number[] | null = null){
        this._activeWorkstepId = id
        this._newWorkstepSelected.next(id)

        //id der bereits angezeigten Zeiten in queryString umwandeln
        var excludedTimeIdsString: string = "?excludedTimeIds=0"
        // if(runningTimeIds != null) excludedTimeIdsString = `?excludedTimeIds=${runningTimeIds.join("-")}` 

        const result: HttpFunctionResult<any> = await this.getAPIData<any>(`ticket/worksteps/${id}${excludedTimeIdsString}`)
        if(result.success){
            this._ticketArt = result.data.ticketArt as EditArt
            this._kontaktart = result.data.contactArt as EditArt
            this._process = result.data.process as number
            this._berechnen = this.getToCalculateById(result.data.toCalculate as number)
            this._contactPerson = result.data.contactPerson
            this._targetTime = result.data.targetTime

            //Tags mit Farben erstellen
            this._tags = ((result.data?.tags as TicketTag[]) || [] ).map(tag => {
                const rgb: { r: number; g: number; b: number; } = this.darkmode.hexToRgb(tag.color)
                return {
                    id: tag.id,
                    description: tag.description,
                    hex: tag.color,
                    rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`
                  }
            })

            this._runningExternalTime = result.data?.externalTimes?.map((eT: TicketZeit)=>({
                    ticket: {
                        id: this.ticket.id,
                        description: this.ticket.description
                    },
                    time: eT,
                    workspaceId: this.activeWorkstepId,
                    taskId: eT?.taskId,
                    taskDescription: eT?.taskDescription
                })
            ) || []
        }
        return result
    }

        /**
     * setzt den die Inhalte eines Arbeitsschrittes
     * @param {number} id id des Arbeitsschrittes
     * @returns 
     */
    public async setWorkstepContent(id: number, searchText: string = ""): Promise<HttpFunctionResult<any>>{
        if(this.contentOffset==0) {
            this._workstepContent = []
            this._workstepContentWithAttachment = []
        }
        const result: HttpFunctionResult<WorkstepContent[]> = await this.getAPIData<WorkstepContent[]>(`ticket/worksteps/${id}/content?offset=${this.contentOffset}&searchtext=${encodeURIComponent(searchText)}`)
        if(result.success) this._workstepContent = [...this._workstepContent, ...result.data]
        this.sortWorkstepContentWithAttachments(searchText, result.data, this.contentOffset == 0)
        if(result.data.length < 25) this.workstepCanMoreLoad = false
        return result
    }

              /**
     * Erstellt neuen Arbeitsschritt-Content
     * @param {WorkstepContent} content zu erstellenden content 
     * @returns 
     */
    public async createNewWorkstepContent(content: WorkstepContent, sendMailToCustomers: boolean = false): Promise<HttpFunctionResult<WorkstepContent>>{
        const result: HttpFunctionResult<WorkstepContent> = await this.http.POST<WorkstepContent, WorkstepContent>({
            path: `ticket/worksteps/content?sendCustomerMail=${sendMailToCustomers}`,
            body: content
        })

        if(result.success){
            let newContent: WorkstepContent = result.data as WorkstepContent
            this._workstepContent.unshift(newContent)
            this._workstepContentWithAttachment.unshift({type: "content", content: newContent})
            await this.socketSrv.invoke("NewWorkstepContent", `ticket_${this.ticket.id}`, content.workstepId, result.data.id)
        }

        return result
      }

          /**
     * Löscht einen bestehenden Arbeitsschritt Content
     * @param {WorkstepContent} id id des contents
     * @returns 
     */
    public async deleteWorkstepContent(content: WorkstepContent): Promise<HttpFunctionResult<any>>{
        const result: HttpFunctionResult<any> = await this.http.DELETE({path: `ticket/worksteps/content/${content.id}`})
        if(result.success){
            this._workstepContent.splice(this._workstepContent.indexOf(content), 1)

            this._workstepContentWithAttachment.splice(this._workstepContentWithAttachment.findIndex((w => {
                if(w.type == "attachment") return
                return w.content.id == content.id
              })), 1)
        }
        return result
      }

    /**
     * Fügt eine neue Datei in Liste hinzu
     * @param {Attachment} file Zu hinzuzufügende Datei 
     * @returns 
     */
    public addFile(file: Attachment){
        if(!file) return
        this._ticket.attachments.push({workstepId: this.activeWorkstepId, ...file})

        if(file.type != "Geräte")
            this._workstepContentWithAttachment.unshift({type: "attachment", attachment: {workstepId: this.activeWorkstepId, ...file}})
    }
    
    /**
     * Erstellt ein neues Ticket in der Datenabnk und gibt dieses zurück
     * @param {NewTicket} newTicket Ticket mit Daten welches ansgelegt werden soll
     * @returns {Promise<HttpFunctionResult<TicketBase>>} Neu erzeugtes BaseTicket
     */
    public async createNewTicket(newTicket: NewTicket): Promise<HttpFunctionResult<TicketBase>>{
        return new Promise(async resolve=>{
            await this.http.POST<NewTicket, TicketBase>({
                path: "ticket",
                body: newTicket,
                statusResult: (status: number, result: HttpFunctionResult<TicketBase>)=>{
                    //Demo
                    if(status == 204) return resolve({success: false, data: {number: "DEMO"}})
                    return resolve(result)
                }
            })
        })
    }
    /**
     * wandelt eine ID zu einem EditArt objekt
     * @param {number} id Id von zu berechnen
     * @returns {EditArt}
     */
    public getToCalculateById(id: number): EditArt{
        var art: EditArt = {id: id, description: ""}
        switch(id){
            case 0:
                art.description = "Nicht berechnen"
                break
            case 1:
                art.description = "Zu berechnen"
                break
            case 2: 
                art.description = "Bereits berechnet"
                break
            default: 
                art.description = "Manuell"
        }
        return art
    }

    /**
     * Sortiert die Contents des Arbeitsschrittes und die Anhänge nach datum In Array
     */
    public sortWorkstepContentWithAttachments(searchText: string, contents: WorkstepContent[], isFirst: boolean) {
        let newContentWithAttachments: WorkstepContentWithAttachment[] = []
        newContentWithAttachments = contents.map((content) => ({type: "content", content: content})) as WorkstepContentWithAttachment[]
        const attachments: Attachment[] = this.ticket.attachments.filter(a => {
            if(this._workstepContentWithAttachment.slice(-25).find(att => att?.type == "attachment" ? a.id == att.attachment.id : false)) return false
            return dayjs(a?.date).isBetween(contents.length < 25 ? dayjs().add(-100, "years") : dayjs(contents[contents.length - 1]?.date), 
            isFirst ? dayjs().add(1, "days") : 
                contents.length == 0 ? dayjs(
                    this.workstepContent.length > 0 ? this.workstepContent[this.workstepContent.length -1].date : new Date()
                ) : dayjs(contents[0].date)) == true
        })

        // this._workstepContentWithAttachment = this.workstepContent.map((content) => ({type: "content", content: content}))
        newContentWithAttachments = newContentWithAttachments.concat(attachments.map(attachment=>{
            if(!attachment.description.toLowerCase().includes(searchText.toLowerCase())) return
            return {type: "attachment", attachment: attachment}
        }))
        newContentWithAttachments = newContentWithAttachments.sort((a,b)=>{
            const date1: Date = new Date(a.type == "attachment" ? a.attachment?.date : a.content?.date)
            const date2: Date = new Date(b.type == "attachment" ? b.attachment?.date : b.content?.date)
            return date2.getTime() - date1.getTime()
        })
        this._workstepContentWithAttachment = [...this._workstepContentWithAttachment, ...newContentWithAttachments]
    }  

    /**
     * Registriert die Socket-Ereignisse für ein Ticket.
     * 
     * @param ticket - Das Ticket, für das die Socket-Ereignisse registriert werden sollen.
     * @returns Eine Promise, die das Ergebnis der Operation enthält.
     */
    public async registerTicketSocketEvents(ticket: TicketBase){
        if(this.socketSrv.socketState != signalR.HubConnectionState.Connected) await this.socketSrv.initializeSocket()
        if(this.lastTicketId) this.socketSrv.leaveGroup(`ticket_${this.lastTicketId}`)
        this.lastTicketId = ticket.id 
        this.socketUser = []
        this.socketSrv.joinTicketGroup<ISocketUser | SocketNewWorkstep>(`ticket_${ticket.id}`, async (data: SocketMessageData<ISocketUser | SocketNewWorkstep>)=>{
          this.lastTicketId = ticket.id 
          if(!data) return
          if(data.eventName == "Joined"){
            if(this.socketUser.find(x => x.userId == (data.data as ISocketUser).userId)) return
            this.socketUser.push(SocketUser.initByObject(data.data as ISocketUser))
          } else if(data.eventName == "Left"){
            this.socketUser = this.socketUser.filter(x => x.userId != (data.data as ISocketUser).userId)
          } else if(data.eventName == "NewWorkstepContent"){
            const workstepData: SocketNewWorkstep = data.data as SocketNewWorkstep
            if(workstepData.workstepId != this.activeWorkstepId) return
            const result: HttpFunctionResult<WorkstepContent[]> = await this.http.GET({
                path: `ticket/worksteps/${workstepData.workstepId}/content?contentId=${workstepData.contentId}`,
                handleError: false
            })
            if(result.success && result?.data?.length > 0){
                this._workstepContent.unshift(result.data[0])
                this._workstepContentWithAttachment.unshift({type: "content", content: result.data[0]})
            }
          }
        })
    
        this.socketSrv.removeHandler("UserDisconnected")
        this.socketSrv.registerHandler("UserDisconnected", (data: SocketMessageData<string>)=>{
          this.socketUser = this.socketUser.filter(x => x.connectionId != data.data)
        })

        const result = await this.socketSrv.invoke("GetTicketGroupMembers", `ticket_${ticket.id}`)
        if(result) this.socketUser = result.map(x => SocketUser.initByObject(x)).filter(x => x.userId != this.auth.getUser().id)
      }
    
    /**
     * Gibt den Icon-Namen für eine Art anhand eines Index zurück
     * @param {number} index Index den Icon-Namens
     */
    public getEditArtIconByIndex(index: number): string{
        switch (index) {
          case 0: return "fa-phone"
          case 1: return "fa-comment"
          case 2: return "fa-fax"
          case 3: return "fa-mail-unread"
          case 4: return "fa-globe"
          case 5: return "fa-pin"
          case 6: return "fa-user-business"
          case 7: return "fa-basket"
          case 8: return "fa-cube"
          case 9: return "fa-message"
          case 10: return "fa-database"
          default: return "fa-8bit-alien"
        }
      }
}
